/* eslint-disable no-console */
import { FormErrors, Locales } from "@simplyk/common";
import { v4 } from "uuid";

import { MinimalLayout } from "../../common/layouts/MinimalLayout";

import { addCacheTagHeaderToServerResponse } from "@/common/helpers/cache";
import { getSSRNewsletterForm, getTranslation, provideOrganization } from "@/common/helpers/getStaticProps";
import { DASHBOARD_TRANSLATION_FILES } from "@/common/helpers/translationFiles";
import { usePageScrollUp } from "@/common/hooks/usePageScrollUp";
import { useTheme } from "@/common/hooks/useTheme";
import { ServerSideContext } from "@/common/types/app";
import { OrganizationOutput, NewsletterFormFrontendPageOutput } from "@/common/types/trpc";
import { AppFormProvider } from "@/components/AppProvider/AppFormProvider/AppFormProvider";
import { ZeffyHead } from "@/components/Head/ZeffyHead";
import NotFoundWithProviders from "@/components/NotFound/NotFoundWithProviders";
import { NewsletterFormContainer } from "@/features/NewsletterForm/Signup/NewsletterFormContainer";

type NewsletterFormWithOrganization = NewsletterFormFrontendPageOutput & {
  organization: OrganizationOutput;
};

type NewsletterFormPageProps =
  | {
      newsletterForm: NewsletterFormWithOrganization;
      isDeleted?: false;
    }
  | {
      newsletterForm?: null;
      isDeleted: true;
    };
const NewsletterFormPage = ({ newsletterForm, isDeleted }: NewsletterFormPageProps) => {
  usePageScrollUp();

  const theme = useTheme({ color: newsletterForm?.buttonColor });

  if (isDeleted) {
    return (
      <>
        <ZeffyHead />
        <NotFoundWithProviders notFoundEntity="signUpForm" />
      </>
    );
  }

  return (
    <>
      {/* Nothing should wrap or block the Head component */}
      <ZeffyHead title={newsletterForm.title} description="" />
      <AppFormProvider
        theme={theme}
        organization={newsletterForm.organization}
        fields={[{ locale: newsletterForm.locale }]}
        themeColor={newsletterForm.buttonColor}
        formData={null}
        formType={null}
        SSREnabled={false} // Newsletter has issues with translations when SSR is enabled, do not enable until issue is fixed
      >
        <MinimalLayout hideBackground>
          <NewsletterFormContainer newsletterForm={newsletterForm} />
        </MinimalLayout>
      </AppFormProvider>
    </>
  );
};

export const getServerSideProps = async (context: ServerSideContext<{ newsletterFormId: string }>) => {
  const requestId = (context.req?.headers?.["x-request-id"] as string) || v4();
  const { newsletterFormId } = context.params;

  const locale = context.locale.toUpperCase() as Locales;

  try {
    const [newsletterFormResponse, translations] = await Promise.all([
      getSSRNewsletterForm({ newsletterFormIdOrUrlPath: newsletterFormId, requestId }),
      getTranslation(context.locale, DASHBOARD_TRANSLATION_FILES),
    ]);

    if (newsletterFormResponse.error) {
      const code = newsletterFormResponse.error.code;

      if (code === FormErrors.FormDeleted) {
        if (context.res) {
          context.res.statusCode = 410;
        }

        console.log(`Form deleted - redirecting to gone newsletterFormId=${newsletterFormId} ssrId=${requestId}`);
        return {
          props: {
            ...translations,
            isDeleted: true,
          },
        };
      }

      console.log(
        `NEXT_ERROR: form not found - redirecting to notFound newsletterFormId=${newsletterFormId} ssrId=${requestId}`
      );
      return { props: {}, notFound: true };
    }

    const cacheTags = [newsletterFormId];
    if (newsletterFormResponse.data?.id && newsletterFormResponse.data.id !== newsletterFormId) {
      cacheTags.push(newsletterFormResponse.data.id);
    }
    addCacheTagHeaderToServerResponse(context.res, cacheTags);

    return {
      props: {
        ...translations,
        newsletterForm: newsletterFormResponse.data.organization
          ? newsletterFormResponse.data
          : provideOrganization(newsletterFormResponse.data, locale),
      },
    };
  } catch (error) {
    console.log(
      `NEXT_ERROR: request failed - redirecting to notFound newsletterFormId=${newsletterFormId} ssrId=${requestId}`,
      error
    );
    return { props: {}, notFound: true };
  }
};

export default NewsletterFormPage;
